import { useState, useEffect, useCallback } from 'react'
import { useMutation } from "@tanstack/react-query"

import { showLoading } from 'services/util'
import checkmark from 'images/check-green.svg'

import { createFixedItemMutation } from '../../graphql/Mutations'
import { graphql  } from '../../api'

import css from '../sass/createedit.module.scss'
import themeCss from '../../components/sass/theme.module.scss'
import UploadFiles from '../UploadFiles'
import { handleEscapeKey } from '../../components/utils'
import TextEditor from '../TextEditor'
import DuplicatedTitleWarning from '../DuplicatedTitleWarning'
import { normalizeRichTextForStorage } from '../auctionsadminutil'

export default function CreateFixedItem({ auction, refreshAdminAuctionQuery, setHideCloseModalButton, setShowFixedItemModal }) {
  // item Id to be auto-generated in resolver using util.autoId()
  const auctionId = auction.AuctionId

  if (!setHideCloseModalButton) setHideCloseModalButton = () => {}

  const [description, setDescription] = useState('')
  const [donor, setDonor] = useState('')
  const [category, setCategory] = useState()
  const [imageFilename, setImageFilename] = useState('')
  const [itemCode, setItemCode] = useState(Math.floor(Math.random() * 10000) + 10000) // TO DO - faking high codes for organization during development
  const [purchasePrice, setPurchasePrice] = useState('')
  const [totalItemsAvailable, setTotalItemsAvailable] = useState('')
  const [limitPerPerson, setLimitPerPerson] = useState('')
  const [title, setTitle] = useState('')
  const [value, setValue] = useState('')

  const [itemCodes, setItemCodes] = useState()
  const [createSuccess, setCreateSuccess] = useState(false)

  const [isLimitPerPersonValid, setIsLimitPerPersonValid] = useState(true)
  const [isTotalItemsAvailableValid, setIsTotalItemsAvailableValid] = useState(true)

  const [disableCode, setDisableCode] = useState(false)
  const [disableSubmit, setDisableSubmit] = useState(true)

  const [createdItem, setCreatedItem] = useState()

  const [isImageSelected, setIsImageSelected] = useState(false)
  const [isImageUploaded, setIsImageUploaded] = useState(false)

  const italicLabel = '<i>text to italicize</i>'

  const categories = auction?.ItemCategories || []

  const {data: createItemResponse, error, isPending, mutate: createItem } = useMutation({
    mutationFn: (input) => {
      return graphql({query: createFixedItemMutation, variables: {input: input} })
    },
  })

  // type "number" still allows "e" and "-" to be entered
  // forces only positive numbers
  const handleNumberChange = (setter) => (e) => {
    let onlyNumbers = e.target.value.replace(/[^0-9]/g, "")
    // prevent if they copy and paste from somewhere, or try to enter 0's at the beginning
    if (onlyNumbers[0] === '0') setter(onlyNumbers.replace(/^0+/, ""))
    else setter(onlyNumbers)
  }

  const toggleModal = useCallback(() => {
    setShowFixedItemModal(false)
  }, [setShowFixedItemModal])

  useEffect(() => {
    const onKeyDown = (event) => handleEscapeKey(event, toggleModal)
    //add event listener for when modal is mounted
    document.addEventListener('keydown', onKeyDown)
    //clean up when modal is unmounted
    return () => document.removeEventListener('keydown', onKeyDown)

  }, [toggleModal])

  useEffect(() => {
    if (limitPerPerson === 0 || limitPerPerson === '0') setIsLimitPerPersonValid(false)
    else setIsLimitPerPersonValid(true)
  }, [limitPerPerson])

  useEffect(() => {
    if (totalItemsAvailable === 0 || totalItemsAvailable === '0') setIsTotalItemsAvailableValid(false)
    else setIsTotalItemsAvailableValid(true)
  }, [totalItemsAvailable])

  const onSubmit = async (e) => {
    e.preventDefault()

    const normalizedDescription = normalizeRichTextForStorage(description)

    const input = {
      AuctionId: auctionId,
      Title: title.trim(),
      ImageFilename: imageFilename.trim(),
      Category: category,
      Donor: donor.trim(),
      ItemCode: itemCode,
      Description: normalizedDescription,
      Value: value,
      PurchasePrice: purchasePrice,
      TotalItemsAvailable: totalItemsAvailable,
      LimitPerPerson: limitPerPerson,
    }

    if (totalItemsAvailable === '') input.TotalItemsAvailable = null
    if (limitPerPerson === '') input.LimitPerPerson = null

    console.log('input', input)

    setCreatedItem(input)
    createItem(input)
  }

  useEffect(() => {
    showLoading(isPending, "CreateItem")
  }, [isPending])

  useEffect(() => {
    if (error) {
      console.log("Item not added: ", error)
      console.log(error.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
    }
    else if (createItemResponse) {
      console.log(createItemResponse)
      console.log('Item added: ', createItemResponse.data.createFixedItem.ItemId)
      const normalizedDescription = normalizeRichTextForStorage(description)
      const newItem = {
          AuctionId: auctionId,
          Title: title.trim(),
          ImageFilename: imageFilename.trim(),
          Category: category,
          Donor: donor.trim(),
          ItemCode: itemCode,
          PurchasePrice: purchasePrice,
          TotalItemsAvailable: totalItemsAvailable,
          LimitPerPerson: limitPerPerson,
          Description: normalizedDescription,
          Value: value,
          FixedItemId: createItemResponse.data.createFixedItem.FixedItemId}
      setCreatedItem(newItem)
      refreshAdminAuctionQuery()
      setCreateSuccess(true)
    }
  }, [createItemResponse, error, refreshAdminAuctionQuery, auctionId, title, imageFilename, donor, category,
      itemCode, description, purchasePrice, totalItemsAvailable, limitPerPerson, value])

  //setting item codes 
  useEffect(() => {
    if (auction) {
      let codes = auction.Items.map(item => item.ItemCode)
      setItemCodes(codes)
    }
  }, [auction])

  useEffect(() => {
    if (itemCodes?.includes(itemCode)) setDisableCode(true)
    else setDisableCode(false)
  }, [itemCodes, itemCode])

  //use effect to enable submit button when all fields are filled
  useEffect(() => {
    if (title && description && value && purchasePrice && donor && itemCode && isLimitPerPersonValid && isTotalItemsAvailableValid) {
      setDisableSubmit(false)
    } else {
      setDisableSubmit(true)
    }
  }, [title, description, value, purchasePrice, donor, itemCode, isLimitPerPersonValid, isTotalItemsAvailableValid])

  useEffect(() => {
    setHideCloseModalButton(isImageSelected)
  }, [isImageSelected, setHideCloseModalButton])

  const handleCategoryChange = (e) => {
    if (e.target.value === "No category") setCategory(null)
    else setCategory(e.target.value)
  }

  const imageUploadResult = (success) => {
    setIsImageUploaded(success)
  }

  if (createSuccess) {
    return (
      <>
        {!isImageSelected && !isImageUploaded &&
          <div className={css['success-container']}>
            <img className={css['success-checkmark']} src={checkmark} alt={'Item create success'}/>
            <p>Item created successfully!</p>
          </div>}

        {isImageUploaded &&
          <div className={css['success-container']}>
            <img className={css['success-checkmark']} src={checkmark} alt={'Item pic uploaded successfully!'}/>
            <p id="imageUploadSuccessMessage">Your image has been uploaded successfully!</p>
          </div>}

        <UploadFiles imageType={'item'} auction={auction} item={createdItem} refreshQuery={refreshAdminAuctionQuery} parentSubmitSuccess={imageUploadResult} setIsImageSelected={setIsImageSelected}/>
      </>
    )
  }

  return (
    <div className={css['page-container']}>
      <form onSubmit={onSubmit}>
        <h2>Add Fixed-Price Item for {auction.Title}</h2>
        <p className={css['label-notice']}>"Total Items Available" and "Item Quantity Limit per person" can be left blank to allow for no limit for that value.</p>
        <p className={css['label-notice']}>All other fields (excluding Category) are required to create an item.</p>
        <div className={`${css['item-container']}`} >
          <div className={css['create-item-input']}>
            <label>Title<span className={css['asterisk']}>*</span>
            </label>
            <input id="itemTitleInput"
              type='text'
              placeholder='e.g. Tickets to L.A. Opera'
              value={title}
              onChange={(e) => setTitle(e.target.value)}/>
            <DuplicatedTitleWarning title={title} items={auction?.FixedItems} />
          </div>
          <div className={css['create-item-input']}>
            <TextEditor value={description} onChange={setDescription} needsAsterisk={true} editorId="itemDescriptionInput" />
          </div>
          <div className={css['create-item-input']}>
            <label>Category <span className={css['acceptable-text']}>(Optional)</span></label>
            {categories.length > 0 && 
              <select
                id="itemCategorySelect"
                value={category}
                onChange={handleCategoryChange}
                className={css['create-item-input-dropdown']}
              >
                <option value={null}>No category</option>

                {categories.map((category, index) => (
                  <option key={index} value={category}>
                    {category}
                  </option>
                ))}
              </select>}
            {categories.length === 0 && <p className={css['acceptable-text']}>No categories created, add Categories in the Items & Donations tab.</p>}  
          </div>
          <div className={css['create-item-input']}>
            <label>Fair Market Value (in dollars)<span className={css['asterisk']}>*</span></label>
              <input id="itemFairMarketValueInput"
                type='text'
                placeholder='e.g. 120'
                value={value}
                onChange={handleNumberChange(setValue)}/>
            <p className={css['acceptable-text']}>Must be at least 1 dollar</p>
          </div>
          <div className={css['create-item-input']}>
            <label>Fixed Item Purchase Price (in dollars)<span className={css['asterisk']}>*</span> </label>
              <input id="itemPurchasePriceInput"
                type='text'
                placeholder='e.g. 15'
                value={purchasePrice}
                onChange={handleNumberChange(setPurchasePrice)}/>
          </div>
          <div className={css['create-item-input']}>
            <label>Total Items Available <span className={css['acceptable-text']}>(leave blank for unlimited)</span></label>
              <input id="itemTotalItemsAvailableInput"
                type='text'
                placeholder='e.g. 50'
                value={totalItemsAvailable}
                onChange={handleNumberChange(setTotalItemsAvailable)}/>
              {!isTotalItemsAvailableValid && <p className={css['label-notice']}>Value cannot be 0. Enter a positive integer or leave blank for Unlimited.</p>}
          </div>
          <div className={css['create-item-input']}>
            <label>Item Quantity Limit per person  <span className={css['acceptable-text']}>(leave blank for unlimited)</span></label>
              <input id="itemLimitPerPersonInput"
                type='text'
                placeholder='e.g. 5'
                value={limitPerPerson}
                onChange={handleNumberChange(setLimitPerPerson)}/>
              {!isLimitPerPersonValid && <p className={css['label-notice']}>Value cannot be 0. Enter a positive integer or leave blank for Unlimited.</p>}
          </div>
          <div className={css['create-item-input']}>
            <label>Item Number <span className={css['label-notice']}>(randomly assigned, not-editable after initialization)</span>
              <span className={css['asterisk']}>*</span>
            </label>
              <input id="itemCodeInput"
                type='text'
                placeholder='e.g. 5471'
                value={itemCode}
                onChange={handleNumberChange(setItemCode)}/>
                {disableCode && <span className={css['error-text']}>Item Number already in use, select another!</span>}
          </div>
          <div className={css['create-item-input']}>
            <label>Donor<span className={css['asterisk']}>*</span></label>
              <input id="itemDonorInput"
                type='text'
                placeholder='e.g. UCLA Athletics'
                value={donor}
                onChange={(e) => setDonor(e.target.value)}/>
          </div>
          <p>Images to be uploaded on next page</p>
          <p className={css['acceptable-text']}>
            Accepted file types: .jpg, .jpeg, .png, .avif
            <br />
            Images should be in a 16:9 ratio (e.g., 640x360 px) for best display.
          </p>
        </div>
        <button className={`${themeCss['button']} ${themeCss['FilledBlue']} ${themeCss['centered-button']}`}
          disabled={disableCode || disableSubmit} id="saveItemSubmit" type='submit'>Create Item</button>
      </form>
    </div>
  )
}
